// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Event-Wide Operations Analytics
// Comprehensive analytics across all contractors, road owners,
// debris types, CLINs, subcontractors, disposal sites, and timeline
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Stack, Group, Table,
  Loader, Center,
} from '../../components/gab/GabComponents';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import { fmtDate } from '../../lib/date-format';

// ── Helpers ───────────────────────────────────────────────────
const fmtMoney = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtNum = (n: number) => n.toLocaleString();

// ── Road owner colors ─────────────────────────────────────────
const ROAD_OWNER_COLORS: Record<string, string> = {
  'City of Tampa': '#3b82f6',
  'State/FDOT': '#ef4444',
  'FDOT': '#ef4444',
  'Hillsborough County': '#16a34a',
  'Federal': '#d97706',
  'Private': '#d97706',
  'No Match': '#9ca3af',
};

function getRoadOwnerColor(owner: string): string {
  if (ROAD_OWNER_COLORS[owner]) return ROAD_OWNER_COLORS[owner];
  const lower = owner.toLowerCase();
  if (lower.includes('tampa') || lower.includes('city')) return '#3b82f6';
  if (lower.includes('state') || lower.includes('fdot') || lower.includes('dot')) return '#ef4444';
  if (lower.includes('hillsborough') || lower.includes('county')) return '#16a34a';
  if (lower.includes('federal') || lower.includes('private')) return '#d97706';
  return '#8b5cf6';
}

// ── Sortable header helper ─────────────────────────────────
function SortTh({ label, field, sortField, sortDir, onSort, ta }: {
  label: string; field: string; sortField: string; sortDir: 'asc' | 'desc';
  onSort: (f: string) => void; ta?: 'right' | 'left';
}) {
  return (
    <Table.Th ta={ta} style={{
      cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      color: sortField === field ? '#4B3FFF' : '#64748b',
      position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc',
    }} onClick={() => onSort(field)}>
      {label}
      {sortField === field
        ? (sortDir === 'desc' ? ' \u2193' : ' \u2191')
        : <span style={{ opacity: 0.25 }}> \u2191</span>}
    </Table.Th>
  );
}

// ── Generic sort hook factory ──────────────────────────────
function useSortableTable(defaultField: string, defaultDir: 'asc' | 'desc' = 'desc') {
  const [sortField, setSortField] = useState(defaultField);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultDir);
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const sortFn = (a: any, b: any) => {
    if (!sortField) return 0;
    const va = a[sortField] ?? '';
    const vb = b[sortField] ?? '';
    const n = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? n : -n;
  };
  return { sortField, sortDir, toggleSort, sortFn };
}

export default function DebrisAnalytics() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { contractors, loadZones, loadContractors, loadCrews, loadTrucks } = useDebrisStore();

  // Direct API loading of ALL tickets (bypasses 100-record store pagination)
  const [allTickets, setAllTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  useEffect(() => {
    if (!incidentId) return;
    let cancelled = false;
    setLoadingTickets(true);
    (async () => {
      try {
        const { queryRecords } = await import('../../api/client');
        const { TABLES } = await import('../../api/tables');
        const { FIELDS } = await import('../../api/fields');
        const FT = FIELDS.debrisLoadTickets;
        const PAGE = 500;
        let offset = 0, total = Infinity;
        const all: any[] = [];
        while (offset < total && !cancelled) {
          const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
            limit: PAGE, offset,
            filters: [{ field: FT.incidentId, operator: 'eq' as const, value: incidentId }],
          });
          total = res.total;
          res.records.forEach((r: any) => {
            all.push({
              id: r.record_id,
              incidentId: String(r[FT.incidentId] || ''),
              ticketNumber: String(r[FT.ticketNumber] || ''),
              contractorId: String(r[FT.contractorId] || ''),
              debrisType: String(r[FT.debrisType] || ''),
              calculatedCubicYards: String(r[FT.calculatedCubicYards] || '0'),
              status: String(r[FT.status] || ''),
              disposalSiteId: String(r[FT.disposalSiteId] || ''),
              clinCode: String(r[FT.clinCode] || ''),
              clinDescription: String(r[FT.clinDescription] || ''),
              extendedCost: String(r[FT.extendedCost] || ''),
              transactionRate: String(r[FT.transactionRate] || ''),
              subcontractorName: String(r[FT.subcontractorName] || ''),
              truckNumber: String(r[FT.truckNumber] || ''),
              loadDate: String(r[FT.loadDate] || ''),
              disposalSiteName: String(r[FT.disposalSiteName] || ''),
              pickupLat: String(r[FT.pickupLat] || ''),
              pickupLng: String(r[FT.pickupLng] || ''),
              roadOwnerName: String(r[FT.roadOwnerName] || ''),
              roadName: String(r[FT.roadName] || ''),
              roadClass: String(r[FT.roadClass] || ''),
            });
          });
          offset += PAGE;
        }
        // GAB filter API is unreliable — also filter client-side
        if (!cancelled) setAllTickets(incidentId ? all.filter(t => t.incidentId === incidentId) : all);
      } catch (e) { console.error('Failed to load all tickets:', e); }
      if (!cancelled) setLoadingTickets(false);
    })();
    return () => { cancelled = true; };
  }, [incidentId]);

  useEffect(() => {
    loadZones(); loadContractors(); loadCrews(); loadTrucks();
  }, [loadZones, loadContractors, loadCrews, loadTrucks]);

  const iTickets = incidentId ? allTickets : [];
  const iContractors = incidentId ? contractors : [];

  // ── Contractor ID → name map ──────────────────────────────
  const contMap = useMemo(() =>
    Object.fromEntries(iContractors.map(c => [String(c.id), c.companyName || `Contractor ${c.id}`])),
    [iContractors]);

  // ── Ticket helpers ────────────────────────────────────────
  const getCY = (t: any) => parseFloat(t.calculatedCubicYards) || 0;
  const getCost = (t: any) => parseFloat(t.extendedCost) || 0;

  // ══════════════════════════════════════════════════════════
  // ROW 1: Summary Cards
  // ══════════════════════════════════════════════════════════
  const totalTickets = iTickets.length;
  const totalCY = useMemo(() => iTickets.reduce((s, t) => s + getCY(t), 0), [iTickets]);
  const totalCost = useMemo(() => iTickets.reduce((s, t) => s + getCost(t), 0), [iTickets]);
  const uniqueTrucks = useMemo(() => new Set(iTickets.map(t => t.truckNumber).filter(Boolean)).size, [iTickets]);

  // ══════════════════════════════════════════════════════════
  // ROW 2: Road Ownership Analysis
  // ══════════════════════════════════════════════════════════
  const roadOwnerData = useMemo(() => {
    const map: Record<string, { tickets: number; cy: number; cost: number }> = {};
    iTickets.forEach(t => {
      const owner = t.roadOwnerName || 'No Match';
      if (!map[owner]) map[owner] = { tickets: 0, cy: 0, cost: 0 };
      map[owner].tickets++;
      map[owner].cy += getCY(t);
      map[owner].cost += getCost(t);
    });
    return Object.entries(map)
      .map(([owner, d]) => ({ owner, ...d }))
      .sort((a, b) => b.tickets - a.tickets);
  }, [iTickets]);

  const roadOwnerSort = useSortableTable('tickets');

  // ══════════════════════════════════════════════════════════
  // ROW 3L: Debris Type Distribution
  // ══════════════════════════════════════════════════════════
  const debrisTypeData = useMemo(() => {
    const map: Record<string, { type: string; cy: number; tickets: number }> = {};
    iTickets.forEach(t => {
      const dt = t.debrisType || 'unknown';
      if (!map[dt]) map[dt] = { type: dt, cy: 0, tickets: 0 };
      map[dt].cy += getCY(t);
      map[dt].tickets++;
    });
    return Object.values(map).sort((a, b) => b.cy - a.cy);
  }, [iTickets]);

  // ══════════════════════════════════════════════════════════
  // ROW 3R: CLIN Code Breakdown
  // ══════════════════════════════════════════════════════════
  const clinData = useMemo(() => {
    const map: Record<string, { code: string; description: string; tickets: number; cy: number; cost: number }> = {};
    iTickets.forEach(t => {
      const code = t.clinCode || 'N/A';
      if (!map[code]) map[code] = { code, description: t.clinDescription || '', tickets: 0, cy: 0, cost: 0 };
      map[code].tickets++;
      map[code].cy += getCY(t);
      map[code].cost += getCost(t);
      if (!map[code].description && t.clinDescription) map[code].description = t.clinDescription;
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  }, [iTickets]);

  const clinSort = useSortableTable('cost');

  // ══════════════════════════════════════════════════════════
  // ROW 4: Contractor Performance
  // ══════════════════════════════════════════════════════════
  const contractorData = useMemo(() => {
    const map: Record<string, { contractorId: string; tickets: number; cy: number; cost: number; subs: Set<string>; trucks: Set<string> }> = {};
    iTickets.forEach(t => {
      const cid = t.contractorId || 'unknown';
      if (!map[cid]) map[cid] = { contractorId: cid, tickets: 0, cy: 0, cost: 0, subs: new Set(), trucks: new Set() };
      map[cid].tickets++;
      map[cid].cy += getCY(t);
      map[cid].cost += getCost(t);
      if (t.subcontractorName) map[cid].subs.add(t.subcontractorName);
      if (t.truckNumber) map[cid].trucks.add(t.truckNumber);
    });
    return Object.values(map).map(d => ({
      name: contMap[d.contractorId] || d.contractorId,
      tickets: d.tickets,
      cy: d.cy,
      cost: d.cost,
      subs: d.subs.size,
      trucks: d.trucks.size,
    })).sort((a, b) => b.cost - a.cost);
  }, [iTickets, contMap]);

  const contractorSort = useSortableTable('cost');

  // ══════════════════════════════════════════════════════════
  // ROW 5: Subcontractor Ranking
  // ══════════════════════════════════════════════════════════
  const subcontractorData = useMemo(() => {
    const map: Record<string, { name: string; tickets: number; cy: number }> = {};
    iTickets.forEach(t => {
      const name = t.subcontractorName || 'Direct / Unknown';
      if (!map[name]) map[name] = { name, tickets: 0, cy: 0 };
      map[name].tickets++;
      map[name].cy += getCY(t);
    });
    return Object.values(map)
      .sort((a, b) => b.cy - a.cy)
      .slice(0, 20)
      .map(d => ({ ...d, avgCY: d.tickets > 0 ? d.cy / d.tickets : 0 }));
  }, [iTickets]);

  const subSort = useSortableTable('cy');

  // ══════════════════════════════════════════════════════════
  // ROW 6: Disposal Site Analysis
  // ══════════════════════════════════════════════════════════
  const disposalSiteData = useMemo(() => {
    const map: Record<string, { name: string; tickets: number; cy: number }> = {};
    iTickets.forEach(t => {
      const name = t.disposalSiteName || 'Unknown';
      if (!map[name]) map[name] = { name, tickets: 0, cy: 0 };
      map[name].tickets++;
      map[name].cy += getCY(t);
    });
    return Object.values(map).sort((a, b) => b.cy - a.cy);
  }, [iTickets]);

  const disposalSort = useSortableTable('cy');

  // ══════════════════════════════════════════════════════════
  // ROW 7: Road Class Distribution
  // ══════════════════════════════════════════════════════════
  const roadClassData = useMemo(() => {
    const map: Record<string, { roadClass: string; tickets: number; cy: number; cost: number }> = {};
    iTickets.forEach(t => {
      const rc = t.roadClass || 'Unclassified';
      if (!map[rc]) map[rc] = { roadClass: rc, tickets: 0, cy: 0, cost: 0 };
      map[rc].tickets++;
      map[rc].cy += getCY(t);
      map[rc].cost += getCost(t);
    });
    return Object.values(map).sort((a, b) => b.cy - a.cy);
  }, [iTickets]);

  const roadClassSort = useSortableTable('cy');

  // ══════════════════════════════════════════════════════════
  // ROW 8: Timeline
  // ══════════════════════════════════════════════════════════
  const timeline = useMemo(() => {
    const dates: number[] = [];
    iTickets.forEach(t => {
      if (!t.loadDate) return;
      // loadDate format: MM/DD/YYYY
      const parts = t.loadDate.split('/');
      if (parts.length === 3) {
        const d = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
        if (!isNaN(d.getTime())) dates.push(d.getTime());
      }
    });
    if (dates.length === 0) return { first: null, last: null, days: 0 };
    dates.sort((a, b) => a - b);
    const first = new Date(dates[0]);
    const last = new Date(dates[dates.length - 1]);
    const days = Math.max(1, Math.ceil((last.getTime() - first.getTime()) / 86400000) + 1);
    return { first, last, days };
  }, [iTickets]);

  // ── Loading state ─────────────────────────────────────────
  if (loadingTickets && iTickets.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  // ── Donut chart constants ─────────────────────────────────
  const donutSize = 200;
  const donutRadius = 75;
  const donutStroke = 30;

  return (
    <Stack gap={12}>
      <div>
        <Title order={3} c="black">Debris Operations Analytics</Title>
        <Text size="sm" c="dimmed">Event-wide analytics across all contractors, road owners, and operations</Text>
      </div>

      {/* ═══ ROW 1: Summary Cards ═══ */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <SummaryCard label="Total Tickets" value={fmtNum(totalTickets)} color="#0284c7" />
        <SummaryCard label="Total Cubic Yards" value={fmtNum(Math.round(totalCY))} color="#16a34a" />
        <SummaryCard label="Total Cost" value={fmtMoney(totalCost)} color="#7c3aed" />
        <SummaryCard label="Unique Trucks" value={fmtNum(uniqueTrucks)} color="#d97706" />
      </SimpleGrid>

      {/* ═══ ROW 2: Road Ownership Analysis ═══ */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} fz={16} mb="md">Road Ownership Breakdown</Text>
        <Text size="xs" c="dimmed" mb="md">FEMA eligibility analysis — shows ticket distribution by road jurisdiction owner</Text>

        {roadOwnerData.length === 0 ? (
          <Text size="sm" c="dimmed">No road ownership data available</Text>
        ) : (
          <Stack gap="lg">
            {/* Horizontal bar chart */}
            <svg width="100%" height={roadOwnerData.length * 36 + 4} viewBox={`0 0 600 ${roadOwnerData.length * 36 + 4}`} style={{ display: 'block' }}>
              {(() => {
                const maxVal = Math.max(...roadOwnerData.map(d => d.tickets));
                return roadOwnerData.map((d, i) => {
                  const barW = maxVal > 0 ? (d.tickets / maxVal) * 350 : 0;
                  const pct = totalTickets > 0 ? ((d.tickets / totalTickets) * 100).toFixed(1) : '0';
                  const color = getRoadOwnerColor(d.owner);
                  return (
                    <g key={d.owner} transform={`translate(0, ${i * 36 + 2})`}>
                      <text x={145} y={18} textAnchor="end" fontSize={12} fill="#475569" fontWeight={500}>{d.owner}</text>
                      <rect x={150} y={3} width={barW} height={24} rx={4} fill={color} opacity={0.85} />
                      <text x={155 + barW} y={18} fontSize={11} fill="#64748b">{fmtNum(d.tickets)} ({pct}%)</text>
                    </g>
                  );
                });
              })()}
            </svg>

            {/* Road owner detail table */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 500px)' }}>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <SortTh label="Road Owner" field="owner" sortField={roadOwnerSort.sortField} sortDir={roadOwnerSort.sortDir} onSort={roadOwnerSort.toggleSort} />
                    <SortTh label="Tickets" field="tickets" sortField={roadOwnerSort.sortField} sortDir={roadOwnerSort.sortDir} onSort={roadOwnerSort.toggleSort} ta="right" />
                    <SortTh label="CY" field="cy" sortField={roadOwnerSort.sortField} sortDir={roadOwnerSort.sortDir} onSort={roadOwnerSort.toggleSort} ta="right" />
                    <SortTh label="Cost" field="cost" sortField={roadOwnerSort.sortField} sortDir={roadOwnerSort.sortDir} onSort={roadOwnerSort.toggleSort} ta="right" />
                    <Table.Th ta="right" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>% of Total</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {[...roadOwnerData].sort(roadOwnerSort.sortFn).map(d => (
                    <Table.Tr key={d.owner}>
                      <Table.Td>
                        <Group gap="xs">
                          <div style={{ width: 10, height: 10, borderRadius: 4, backgroundColor: getRoadOwnerColor(d.owner) }} />
                          <Text size="sm" fw={500}>{d.owner}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td ta="right"><Text size="sm">{fmtNum(d.tickets)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm">{fmtNum(Math.round(d.cy))}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" ff="monospace">{fmtMoney(d.cost)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm">{totalTickets > 0 ? ((d.tickets / totalTickets) * 100).toFixed(1) : '0'}%</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          </Stack>
        )}
      </Paper>

      {/* ═══ ROW 3: Debris Type + CLIN Breakdown ═══ */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* LEFT: Debris Type Distribution */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="md">Debris Type Distribution</Text>
          {debrisTypeData.length === 0 ? (
            <Text size="sm" c="dimmed">No ticket data yet</Text>
          ) : (
            <Stack gap="md" align="center">
              <svg width={donutSize} height={donutSize} viewBox={`0 0 ${donutSize} ${donutSize}`}>
                {(() => {
                  let cumAngle = -90;
                  const cx = donutSize / 2;
                  const cy = donutSize / 2;
                  return debrisTypeData.map(d => {
                    const pct = totalCY > 0 ? d.cy / totalCY : 0;
                    const angle = pct * 360;
                    const startRad = (cumAngle * Math.PI) / 180;
                    const endRad = ((cumAngle + angle) * Math.PI) / 180;
                    cumAngle += angle;
                    const largeArc = angle > 180 ? 1 : 0;
                    const x1 = cx + donutRadius * Math.cos(startRad);
                    const y1 = cy + donutRadius * Math.sin(startRad);
                    const x2 = cx + donutRadius * Math.cos(endRad);
                    const y2 = cy + donutRadius * Math.sin(endRad);
                    const color = DEBRIS_TYPE_COLORS[d.type as DebrisType] ?? '#6b7280';
                    if (pct < 0.001) return null;
                    return (
                      <path
                        key={d.type}
                        d={`M ${x1} ${y1} A ${donutRadius} ${donutRadius} 0 ${largeArc} 1 ${x2} ${y2}`}
                        fill="none"
                        stroke={color}
                        strokeWidth={donutStroke}
                        strokeLinecap="butt"
                      >
                        <title>{DEBRIS_TYPE_LABELS[d.type as DebrisType] ?? d.type}: {fmtNum(Math.round(d.cy))} CY ({(pct * 100).toFixed(1)}%)</title>
                      </path>
                    );
                  });
                })()}
                <text x={donutSize / 2} y={donutSize / 2 - 6} textAnchor="middle" fontSize="18" fontWeight="800" fill="#1e293b">
                  {fmtNum(Math.round(totalCY))}
                </text>
                <text x={donutSize / 2} y={donutSize / 2 + 12} textAnchor="middle" fontSize="11" fill="#94a3b8">
                  Total CY
                </text>
              </svg>
              <Stack gap={4} style={{ width: '100%' }}>
                {debrisTypeData.map(d => {
                  const pct = totalCY > 0 ? (d.cy / totalCY) * 100 : 0;
                  const color = DEBRIS_TYPE_COLORS[d.type as DebrisType] ?? '#6b7280';
                  return (
                    <Group key={d.type} justify="space-between">
                      <Group gap="xs">
                        <div style={{ width: 10, height: 10, borderRadius: 4, backgroundColor: color }} />
                        <Text size="sm" fw={500}>{DEBRIS_TYPE_LABELS[d.type as DebrisType] ?? d.type}</Text>
                      </Group>
                      <Text size="sm" c="dimmed">{fmtNum(Math.round(d.cy))} CY ({pct.toFixed(1)}%)</Text>
                    </Group>
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Paper>

        {/* RIGHT: CLIN Code Breakdown */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="md">CLIN Code Breakdown</Text>
          {clinData.length === 0 ? (
            <Text size="sm" c="dimmed">No CLIN data available</Text>
          ) : (
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 500px)' }}>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <SortTh label="CLIN Code" field="code" sortField={clinSort.sortField} sortDir={clinSort.sortDir} onSort={clinSort.toggleSort} />
                    <Table.Th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>Description</Table.Th>
                    <SortTh label="Tickets" field="tickets" sortField={clinSort.sortField} sortDir={clinSort.sortDir} onSort={clinSort.toggleSort} ta="right" />
                    <SortTh label="CY" field="cy" sortField={clinSort.sortField} sortDir={clinSort.sortDir} onSort={clinSort.toggleSort} ta="right" />
                    <SortTh label="Cost" field="cost" sortField={clinSort.sortField} sortDir={clinSort.sortDir} onSort={clinSort.toggleSort} ta="right" />
                    <Table.Th ta="right" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>Avg Rate</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {[...clinData].sort(clinSort.sortFn).map(d => (
                    <Table.Tr key={d.code}>
                      <Table.Td><Text size="sm" fw={600} ff="monospace">{d.code}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{d.description || '\u2014'}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm">{fmtNum(d.tickets)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm">{fmtNum(Math.round(d.cy))}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" ff="monospace">{fmtMoney(d.cost)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" ff="monospace">{d.cy > 0 ? fmtMoney(d.cost / d.cy) : '\u2014'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </Paper>
      </SimpleGrid>

      {/* ═══ ROW 4: Contractor Performance ═══ */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="md">Contractor Performance</Text>
        {contractorData.length === 0 ? (
          <Text size="sm" c="dimmed">No contractor data available</Text>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <SortTh label="Contractor" field="name" sortField={contractorSort.sortField} sortDir={contractorSort.sortDir} onSort={contractorSort.toggleSort} />
                  <SortTh label="Tickets" field="tickets" sortField={contractorSort.sortField} sortDir={contractorSort.sortDir} onSort={contractorSort.toggleSort} ta="right" />
                  <SortTh label="CY" field="cy" sortField={contractorSort.sortField} sortDir={contractorSort.sortDir} onSort={contractorSort.toggleSort} ta="right" />
                  <SortTh label="Cost" field="cost" sortField={contractorSort.sortField} sortDir={contractorSort.sortDir} onSort={contractorSort.toggleSort} ta="right" />
                  <SortTh label="Subcontractors" field="subs" sortField={contractorSort.sortField} sortDir={contractorSort.sortDir} onSort={contractorSort.toggleSort} ta="right" />
                  <SortTh label="Trucks" field="trucks" sortField={contractorSort.sortField} sortDir={contractorSort.sortDir} onSort={contractorSort.toggleSort} ta="right" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {[...contractorData].sort(contractorSort.sortFn).map(d => (
                  <Table.Tr key={d.name}>
                    <Table.Td><Text size="sm" fw={500}>{d.name}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(d.tickets)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(Math.round(d.cy))}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" ff="monospace">{fmtMoney(d.cost)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(d.subs)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(d.trucks)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Paper>

      {/* ═══ ROW 5: Subcontractor Ranking ═══ */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="md">Top 20 Subcontractors</Text>
        {subcontractorData.length === 0 ? (
          <Text size="sm" c="dimmed">No subcontractor data available</Text>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <SortTh label="Subcontractor" field="name" sortField={subSort.sortField} sortDir={subSort.sortDir} onSort={subSort.toggleSort} />
                  <SortTh label="Tickets" field="tickets" sortField={subSort.sortField} sortDir={subSort.sortDir} onSort={subSort.toggleSort} ta="right" />
                  <SortTh label="CY" field="cy" sortField={subSort.sortField} sortDir={subSort.sortDir} onSort={subSort.toggleSort} ta="right" />
                  <SortTh label="Avg CY/Ticket" field="avgCY" sortField={subSort.sortField} sortDir={subSort.sortDir} onSort={subSort.toggleSort} ta="right" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {[...subcontractorData].sort(subSort.sortFn).map(d => (
                  <Table.Tr key={d.name}>
                    <Table.Td><Text size="sm" fw={500}>{d.name}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(d.tickets)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(Math.round(d.cy))}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{d.avgCY.toFixed(1)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Paper>

      {/* ═══ ROW 6: Disposal Site Analysis ═══ */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="md">Disposal Site Analysis</Text>
        {disposalSiteData.length === 0 ? (
          <Text size="sm" c="dimmed">No disposal site data available</Text>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <SortTh label="Site Name" field="name" sortField={disposalSort.sortField} sortDir={disposalSort.sortDir} onSort={disposalSort.toggleSort} />
                  <SortTh label="Tickets" field="tickets" sortField={disposalSort.sortField} sortDir={disposalSort.sortDir} onSort={disposalSort.toggleSort} ta="right" />
                  <SortTh label="CY" field="cy" sortField={disposalSort.sortField} sortDir={disposalSort.sortDir} onSort={disposalSort.toggleSort} ta="right" />
                  <Table.Th ta="right" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>% of Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {[...disposalSiteData].sort(disposalSort.sortFn).map(d => (
                  <Table.Tr key={d.name}>
                    <Table.Td><Text size="sm" fw={500}>{d.name}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(d.tickets)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(Math.round(d.cy))}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{totalCY > 0 ? ((d.cy / totalCY) * 100).toFixed(1) : '0'}%</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Paper>

      {/* ═══ ROW 7: Road Class Distribution ═══ */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="md">Road Classification Distribution</Text>
        {roadClassData.length === 0 ? (
          <Text size="sm" c="dimmed">No road classification data available</Text>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <SortTh label="Road Class" field="roadClass" sortField={roadClassSort.sortField} sortDir={roadClassSort.sortDir} onSort={roadClassSort.toggleSort} />
                  <SortTh label="Tickets" field="tickets" sortField={roadClassSort.sortField} sortDir={roadClassSort.sortDir} onSort={roadClassSort.toggleSort} ta="right" />
                  <SortTh label="CY" field="cy" sortField={roadClassSort.sortField} sortDir={roadClassSort.sortDir} onSort={roadClassSort.toggleSort} ta="right" />
                  <SortTh label="Cost" field="cost" sortField={roadClassSort.sortField} sortDir={roadClassSort.sortDir} onSort={roadClassSort.toggleSort} ta="right" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {[...roadClassData].sort(roadClassSort.sortFn).map(d => (
                  <Table.Tr key={d.roadClass}>
                    <Table.Td><Text size="sm" fw={500}>{d.roadClass}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(d.tickets)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{fmtNum(Math.round(d.cy))}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" ff="monospace">{fmtMoney(d.cost)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </Paper>

      {/* ═══ ROW 8: Timeline ═══ */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="sm">Operations Timeline</Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">First Ticket</Text>
            <Text fw={600}>{timeline.first ? fmtDate(timeline.first) : '\u2014'}</Text>
          </div>
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Last Ticket</Text>
            <Text fw={600}>{timeline.last ? fmtDate(timeline.last) : '\u2014'}</Text>
          </div>
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Active Days</Text>
            <Text fw={600}>{timeline.days > 0 ? `${fmtNum(timeline.days)} days` : '\u2014'}</Text>
          </div>
        </SimpleGrid>
      </Paper>
    </Stack>
  );
}

// ── Summary Card Component ─────────────────────────────────
function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <Paper p="sm" radius="md" withBorder ta="center" style={{
      borderRadius: 12, borderTop: `3px solid ${color}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}>
      <Text size="xs" tt="uppercase" fw={700} c="dimmed">{label}</Text>
      <Text fz={32} fw={800} style={{ color }}>{value}</Text>
    </Paper>
  );
}
